// طلبات الانضمام — مراجعة الإدارة ونوع المسار والصلاحية قبل الاعتماد
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_AR } from "@/v2/lib/rbac";
import { useDebounced, errText } from "@/v2/lib/hooks";
import { GlassCard, Chip, Skeleton, Modal, Table, Pagination, PageHead, Empty } from "@/v2/admin/ui";
import {
  UserPlus, Search, CheckCircle2, XCircle, Building2, Sparkles, ShieldCheck, RotateCcw,
} from "lucide-react";

const CONF = { high: ["emerald", "ثقة عالية"], medium: ["gold", "ثقة متوسطة"], low: ["red", "ثقة منخفضة"] };
const STATUS_TONE = { pending: "gold", approved: "emerald", rejected: "red", suspended: "red", onboarding: "muted" };

function ReviewModal({ row, meta, onClose }) {
  const { user: admin } = useAuth();
  const qc = useQueryClient();
  const [dept, setDept] = useState(row.department_id || "");
  const [type, setType] = useState(row.employee_type || "");
  const [role, setRole] = useState(row.requested_role === "dept_manager" ? "dept_manager" : "employee");
  const [alias, setAlias] = useState(!!row.department_typed);
  const [note, setNote] = useState(row.admin_note || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const done = (msg) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ["join-requests"] });
    qc.invalidateQueries({ queryKey: ["adm-users"] });
    onClose();
  };

  const approve = async () => {
    if (!dept) return toast.error("حدد الإدارة الرسمية");
    if (!type) return toast.error("حدد نوع المسار");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/join-requests/${row.id}/approve`, {
        department_id: dept, employee_type: type, role, add_alias: alias, note,
      });
      done(data.alias_added ? "تم الاعتماد وإضافة الاسم البديل" : "تم اعتماد الحساب");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/join-requests/${row.id}/reject`, { reason });
      done("تم رفض الطلب");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const [ctone, clabel] = CONF[row.match_confidence] || CONF.low;

  return (
    <Modal title="مراجعة طلب الانضمام" onClose={onClose} wide testId="join-review">
      <div className="v2-inset p-4 mb-5">
        <div className="font-extrabold text-lg">{row.name}</div>
        <div className="text-xs text-[#bcc8cf] mt-1">
          تاريخ التسجيل: {(row.created_at || "").slice(0, 10)} · تاريخ الطلب: {(row.submitted_at || "").slice(0, 10)}
        </div>
        {row.department_typed && (
          <div className="mt-3 text-sm">
            <span className="text-[#bcc8cf]">ما كتبه الموظف:</span> <b dir="rtl">«{row.department_typed}»</b>
            <Chip tone={ctone} className="ms-2">{clabel} {row.match_score}%</Chip>
          </div>
        )}
        {row.suggestions?.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-3">
            {row.suggestions.map((s) => (
              <button key={s.id} onClick={() => setDept(s.id)} data-testid={`join-sugg-${s.id}`}
                className={`v2-btn-sm ${dept === s.id ? "v2-btn" : "v2-btn-ghost"}`}>
                {s.name} · {s.score}%
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="v2-label"><Building2 size={12} className="inline" /> الإدارة الرسمية</label>
          <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="join-dept" className="v2-input">
            <option value="">— اختر الإدارة —</option>
            {(meta?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="v2-label"><Sparkles size={12} className="inline" /> نوع المسار</label>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="join-type" className="v2-input">
            <option value="">— اختر النوع —</option>
            {(meta?.employee_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="v2-label"><ShieldCheck size={12} className="inline" /> الصلاحية بعد الاعتماد</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="join-role" className="v2-input"
            disabled={admin?.role !== "super_admin"}>
            <option value="employee">{ROLE_AR.employee}</option>
            <option value="dept_manager">{ROLE_AR.dept_manager}</option>
          </select>
          {admin?.role !== "super_admin" && <p className="text-[10px] text-[#bcc8cf] mt-1">منح صلاحية «مدير إدارة» للمدير الأعلى فقط.</p>}
          {row.requested_role === "dept_manager" && (
            <p className="text-[10px] text-[#e3c799] mt-1">الموظف طلب أن يكون «مدير إدارة».</p>
          )}
        </div>
        <div>
          <label className="v2-label">ملاحظة داخلية</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="join-note" className="v2-input" />
        </div>
      </div>

      {row.department_typed && (
        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="join-alias-label">
          <input type="checkbox" checked={alias} onChange={(e) => setAlias(e.target.checked)} data-testid="join-alias"
            className="w-4 h-4 accent-[#91cab8]" />
          إضافة «{row.department_typed}» كاسم بديل لهذه الإدارة
        </label>
      )}

      <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-white/10">
        <button onClick={approve} disabled={busy} data-testid="join-approve" className="v2-btn flex-1 min-w-[160px]">
          <CheckCircle2 size={16} /> اعتماد الحساب
        </button>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الرفض (اختياري)"
          data-testid="join-reason" className="v2-input !w-auto flex-1 min-w-[160px]" />
        <button onClick={reject} disabled={busy} data-testid="join-reject" className="v2-btn-danger">
          <XCircle size={16} /> رفض
        </button>
      </div>
    </Modal>
  );
}

export default function JoinRequests() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const dSearch = useDebounced(search, 400);

  const { data, isLoading } = useQuery({
    queryKey: ["join-requests", status, dSearch, page],
    queryFn: async () => (await api.get(`/admin/join-requests?status=${status}&search=${encodeURIComponent(dSearch)}&page=${page}&limit=12`)).data,
  });
  const rows = data?.rows || [];

  const resetOnb = async (id, name) => {
    try {
      await api.post(`/admin/users/${id}/reset-onboarding`);
      toast.success(`تم إعادة ضبط بيانات الإعداد لـ${name}`);
      qc.invalidateQueries({ queryKey: ["join-requests"] });
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-join-requests">
      <PageHead icon={UserPlus} title="طلبات الانضمام" sub="مراجعة الإدارة ونوع المسار والصلاحية قبل اعتماد الحساب." />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["pending", "قيد المراجعة"], ["approved", "معتمد"], ["rejected", "مرفوض"],
          ["onboarding", "لم يكمل الإعداد"], ["all", "الكل"]].map(([k, l]) => (
          <button key={k} onClick={() => { setStatus(k); setPage(1); }} data-testid={`join-tab-${k}`}
            className={`v2-inset p-3 text-center transition-all ${status === k ? "!border-[#bb9661]/50 !bg-[#bb9661]/10" : ""}`}>
            <div className="text-lg font-extrabold v2-gold-text">{k === "all" ? (data?.total ?? "—") : (data?.counts?.[k] ?? "—")}</div>
            <div className="text-[10px] text-[#bcc8cf]">{l}</div>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search size={16} className="absolute top-1/2 -translate-y-1/2 right-4 text-[#bcc8cf]" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="join-search"
          placeholder="ابحث باسم الموظف..." className="v2-input" style={{ paddingInlineStart: "2.75rem" }} />
      </div>

      {isLoading ? <Skeleton className="h-64 w-full" /> : rows.length === 0 ? <Empty text="لا توجد طلبات في هذه الحالة." /> : (
        <Table head={["الموظف", "الإدارة", "ما كتبه", "النوع", "الصلاحية المطلوبة", "الحالة", "التاريخ", ""]} min={900} testId="join-table">
          {rows.map((r) => (
            <tr key={r.id} data-testid="join-row">
              <td className="font-semibold">{r.name}</td>
              <td className="text-[#bcc8cf]">{r.department || "—"}</td>
              <td>
                {r.department_typed ? (
                  <span className="flex items-center gap-2">
                    <span className="text-[#bcc8cf] text-xs">«{r.department_typed}»</span>
                    <Chip tone={(CONF[r.match_confidence] || CONF.low)[0]}>{r.match_score}%</Chip>
                  </span>
                ) : <span className="text-[#bcc8cf]">—</span>}
              </td>
              <td><Chip tone="muted">{r.employee_type_label}</Chip></td>
              <td><Chip tone={r.requested_role === "dept_manager" ? "gold" : "muted"}>{r.requested_role_label}</Chip></td>
              <td><Chip tone={STATUS_TONE[r.approval_status] || "muted"}>{r.approval_label}</Chip></td>
              <td className="text-[10px] text-[#bcc8cf]" dir="ltr">{(r.submitted_at || r.created_at || "").slice(0, 10)}</td>
              <td>
                <div className="flex gap-2">
                  <button onClick={() => setSel(r)} data-testid={`join-review-${r.id}`} className="v2-btn-ghost v2-btn-sm">مراجعة</button>
                  {r.approval_status === "approved" && (
                    <button onClick={() => resetOnb(r.id, r.name)} data-testid={`join-reset-${r.id}`}
                      className="v2-btn-ghost v2-btn-sm" title="إعادة ضبط بيانات الإعداد الأولي"><RotateCcw size={13} /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
      {sel && <ReviewModal row={sel} meta={data} onClose={() => setSel(null)} />}
    </div>
  );
}
